import { useContext, useEffect, useMemo, useState } from "react";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";
import moment from "moment";
import { API, graphqlOperation } from "aws-amplify";
import { Button, Card, Grid, Loader, ScrollView } from "@aws-amplify/ui-react";
import { CModal, CModalBody, CModalHeader, CModalTitle } from "@coreui/react";
import AutorenewIcon from "@mui/icons-material/Autorenew";

import {
  ruleLogsByBranchID,
  ruleLogsByCompanyID,
  ruleLogsByDeviceID,
  branchesByCompanyID,
  devicesByBranchID,
  listRuleLogs,
  listCompanies,
} from "graphql/queries";
import Empty from "components/utils/Empty";
import DateRangeForm from "components/utils/DateRangeForm";
import CompaniesTree from "components/companies/CompaniesTree";
import DateRangeInput from "components/utils/DateRangeInput";
import RuleCard from "components/rules/RuleCard";
import { LayoutContext } from "layout/context/LayoutContext";

const RuleLogs = () => {
  const { me } = useContext(LayoutContext);
  const [modalDate, setModalDate] = useState({
    openBy: "",
    visible: false,
  });
  const [filters, setFilters] = useState({
    activatedAt: {
      start: "",
      end: "",
    },
  });
  const [tree, setTree] = useState([]);
  const [filterTree, setFilterTree] = useState("");
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rules, setRules] = useState([]);
  const [nextToken, setNextToken] = useState();
  const [selectedItem, setSelectedItem] = useState({
    queryBy: "all",
    id: "",
  });
  const treeMapped = useMemo(() => {
    return tree
      ?.filter((item) =>
        filterTree
          ? item.name.toLowerCase().includes(filterTree.toLowerCase()) ||
            item.branches?.items?.some(
              (branchItem) =>
                branchItem.name
                  .toLowerCase()
                  .includes(filterTree.toLowerCase()) ||
                branchItem.devices?.items?.some((deviceItem) =>
                  deviceItem.name
                    .toLowerCase()
                    .includes(filterTree.toLowerCase())
                )
            )
          : true
      )
      ?.map((item) => {
        return {
          ...item,
          branches: item.branches?.items?.map((branchItem) => {
            return {
              ...branchItem,
              devices: branchItem.devices?.items?.map((deviceItem) => {
                return {
                  ...deviceItem,
                };
              }),
            };
          }),
        };
      });
  }, [tree, filterTree]);

  const onSelectItem = ({ queryBy, id }, companyID) => {
    if (selectedItem.queryBy === queryBy && selectedItem.id === id) return;
    setSelectedItem({ queryBy, id });
    if (
      queryBy === "companyID" &&
      tree?.some((prevTree) => prevTree.id === id && !prevTree.branches?.items)
    )
      fetchTreeBranches(id);
    if (
      queryBy === "branchID" &&
      tree?.some(
        (prevTree) =>
          prevTree.id === companyID &&
          prevTree.branches?.items?.some(
            (prevBranch) => prevBranch.id === id && !prevBranch.devices?.items
          )
      )
    )
      fetchTreeDevices(id, companyID);
  };

  const onLoadMore = () => {
    fetchRulesWithFilters(filters, selectedItem, nextToken);
  };

  const onSubmitDateRange = (name, start, end) => {
    setFilters((prevSt) => ({
      ...prevSt,
      [name]: {
        start,
        end,
      },
    }));
    setModalDate({ openBy: "", visible: false });
  };

  const onClearDate = (name) => {
    setFilters((prevSt) => ({
      ...prevSt,
      [name]: {
        start: "",
        end: "",
      },
    }));
  };

  async function fetchRules(operation, operationName, operationObj) {
    try {
      setRulesLoading(true);
      const rulesData = await API.graphql(
        graphqlOperation(operation, operationObj)
      );
      const rulesItems = rulesData.data[operationName].items ?? [];
      if (operationObj?.nextToken)
        setRules((prevRules) => [
          ...prevRules,
          ...rulesItems.sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          ),
        ]);
      else
        setRules(
          rulesItems.sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
        );
      setNextToken(rulesData.data[operationName].nextToken);
      setRulesLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setRulesLoading(false);
    }
  }

  async function fetchTree() {
    let filterObj = {};
    try {
      if (me.companyID)
        filterObj = { ...filterObj, or: [{ id: { eq: me.companyID } }] };
      if (me.managedCompanies.length > 0)
        filterObj = {
          ...filterObj,
          or: [
            ...(filterObj?.or ?? []),
            ...me.managedCompanies.map((cp) => ({ id: { eq: cp } })),
          ],
        };

      const companiesData = await API.graphql(
        graphqlOperation(listCompanies, {
          limit: Object.keys(filterObj).length <= 0 ? 20 : undefined,
          filter: {
            ...filterObj,
          },
        })
      );
      const companiesItems = companiesData.data.listCompanies.items;
      setTree(companiesItems);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    }
  }

  async function fetchTreeBranches(companyID) {
    try {
      const treeData = await API.graphql(
        graphqlOperation(branchesByCompanyID, { companyID })
      );
      const treeItems = treeData.data.branchesByCompanyID.items;
      setTree((prevTree) =>
        prevTree?.map((prevItem) =>
          prevItem.id === companyID
            ? { ...prevItem, branches: { items: treeItems } }
            : prevItem
        )
      );
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    }
  }

  async function fetchTreeDevices(branchID, companyID) {
    try {
      const treeData = await API.graphql(
        graphqlOperation(devicesByBranchID, { branchID })
      );
      const treeItems = treeData.data.devicesByBranchID.items;
      setTree((prevTree) =>
        prevTree?.map((prevItem) =>
          prevItem.id === companyID
            ? {
                ...prevItem,
                branches: {
                  items: prevItem.branches?.items?.map((prevBranchItem) =>
                    prevBranchItem.id === branchID
                      ? { ...prevBranchItem, devices: { items: treeItems } }
                      : prevBranchItem
                  ),
                },
              }
            : prevItem
        )
      );
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    }
  }

  const fetchRulesWithFilters = (filters, selectedItem, nextTokenParam) => {
    let operation = undefined;
    let operationName = undefined;
    let operationObj = undefined;

    if (selectedItem.queryBy === "all") {
      operation = listRuleLogs;
      operationName = "listRuleLogs";
      if (me.companyID)
        operationObj = {
          filter: { or: [{ companyID: { eq: me.companyID } }] },
        };
      if (me.managedCompanies.length > 0)
        operationObj = {
          ...operationObj,
          filter: {
            ...operationObj?.filter,
            or: [
              ...(operationObj?.filter?.or ?? []),
              ...me.managedCompanies.map((cp) => ({ companyID: { eq: cp } })),
            ],
          },
        };
    } else if (selectedItem.queryBy === "companyID") {
      operation = ruleLogsByCompanyID;
      operationName = "ruleLogsByCompanyID";
      operationObj = {
        companyID: selectedItem.id,
      };
    } else if (selectedItem.queryBy === "branchID") {
      operation = ruleLogsByBranchID;
      operationName = "ruleLogsByBranchID";
      operationObj = {
        branchID: selectedItem.id,
      };
    } else if (selectedItem.queryBy === "deviceID") {
      operation = ruleLogsByDeviceID;
      operationName = "ruleLogsByDeviceID";
      operationObj = {
        deviceID: selectedItem.id,
      };
    }

    if (filters.activatedAt.start)
      operationObj = {
        ...operationObj,
        filter: {
          ...operationObj?.filter,
          createdAt: {
            ge: moment(filters.activatedAt.start).startOf("day").toISOString(),
            le: moment(filters.activatedAt.end).endOf("day").toISOString(),
          },
        },
      };
    if (!operationObj?.hasOwnProperty("filter"))
      operationObj = {
        ...operationObj,
        limit: 10,
      };
    if (nextTokenParam)
      operationObj = {
        ...operationObj,
        nextToken: nextTokenParam,
      };

    fetchRules(operation, operationName, operationObj);
  };

  useEffect(() => {
    fetchTree();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchRulesWithFilters(filters, selectedItem);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, selectedItem]);

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      <Button
        style={styles.buttonRefresh}
        variation="link"
        onClick={() => fetchRulesWithFilters(filters, selectedItem)}
      >
        <AutorenewIcon /> {strings.refresh}
      </Button>
      <Grid className="mt-3" gap={20} templateColumns={{ medium: "1fr 3fr" }}>
        <Card borderRadius="medium" variation="outlined">
          <CompaniesTree
            tree={treeMapped}
            onSelectAll={() => onSelectItem({ queryBy: "all" })}
            onSelectCompany={(id) => onSelectItem({ queryBy: "companyID", id })}
            onSelectBranch={(id, companyID) =>
              onSelectItem({ queryBy: "branchID", id }, companyID)
            }
            onSelectDevice={(id) => onSelectItem({ queryBy: "deviceID", id })}
            onSearchSubmit={setFilterTree}
          />
        </Card>
        <Grid height="fit-content" gap={10} templateColumns={{ medium: "1fr" }}>
          <Grid className="mb-3" gap={20} templateColumns={{ medium: "1fr" }}>
            <DateRangeInput
              name="activatedAt"
              label={strings.filters.activatedAt}
              start={filters.activatedAt.start}
              end={filters.activatedAt.end}
              onClick={() =>
                setModalDate({ openBy: "activatedAt", visible: true })
              }
              onClear={() => onClearDate("activatedAt")}
            />
          </Grid>
          {rulesLoading ? (
            <Loader />
          ) : rules?.length <= 0 ? (
            <Empty />
          ) : (
            <>
              <ScrollView maxHeight="60vh">
                <Grid gap={20}>
                  {rules?.map((ruleItem) => (
                    <RuleCard item={ruleItem} />
                  ))}
                </Grid>
              </ScrollView>
              {nextToken && (
                <Button onClick={onLoadMore} variation="link">
                  {strings.loadMore}
                </Button>
              )}
            </>
          )}
        </Grid>
      </Grid>
      <CModal
        alignment="center"
        visible={modalDate.visible}
        onClose={() => setModalDate({ openBy: "", visible: false })}
        portal={false}
        backdrop="static"
      >
        <CModalHeader>
          <CModalTitle>{strings.selectDateRange}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <DateRangeForm
            onSubmit={({ from, to }) =>
              onSubmitDateRange(modalDate.openBy, from, to)
            }
          />
        </CModalBody>
      </CModal>
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Reglas",
    search: {
      placeholder: "Buscar...",
      noResults: "No hay resultados",
    },
    responses: {
      errorFetch: "Error al consultar datos",
    },
    companies: "Empresas",
    activatedAt: "Fecha activado",
    filters: {
      activatedAt: "Fecha activado",
    },
    status: {
      activated: "Activado",
    },
    loadMore: "Cargar más...",
    refresh: "Refrescar",
  },
});

const styles = {
  buttonRefresh: { float: "right" },
};

export default RuleLogs;
